/**
 * Bottom sheet on viewports under 768px; centered panel on larger screens. Use for action
 * sheets and short content. For long forms, prefer Modal (scroll region + max height).
 */
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  className = '',
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [touchStart, setTouchStart] = useState<number>(0);
  const [touchCurrent, setTouchCurrent] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  // Controls whether the DOM nodes are mounted at all
  const [isVisible, setIsVisible] = useState(isOpen);
  // Controls whether the "open" CSS classes are applied (drives the transition)
  const [isEntered, setIsEntered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Mount first, then trigger enter transition on the next paint
      setIsVisible(true);
      const raf = requestAnimationFrame(() => {
        setIsEntered(true);
      });
      return () => cancelAnimationFrame(raf);
    } else {
      // Start exit transition, unmount after it completes
      setIsEntered(false);
      const timer = setTimeout(() => setIsVisible(false), 320);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus trap and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }

        if (e.key === 'Tab') {
          const focusableElements = sheetRef.current?.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );

          if (focusableElements && focusableElements.length > 0) {
            const firstElement = focusableElements[0] as HTMLElement;
            const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

            if (e.shiftKey && document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            } else if (!e.shiftKey && document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  // Touch handlers for swipe to dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    setTouchCurrent(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    const deltaY = touchCurrent - touchStart;
    const sheetHeight = sheetRef.current?.offsetHeight || 0;
    const dismissThreshold = Math.min(100, sheetHeight * 0.25);

    if (deltaY > dismissThreshold) {
      onClose();
    }

    setIsDragging(false);
    setTouchStart(0);
    setTouchCurrent(0);
  };

  const dragTransform = isDragging && touchCurrent > touchStart ? touchCurrent - touchStart : 0;

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          isEntered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`fixed z-50 max-h-[85vh] overflow-y-auto bg-white shadow-2xl dark:bg-gray-800 ${className}`}
        style={{
          transition: isDragging
            ? 'none'
            : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.25s ease',
          ...(isDesktop
            ? {
                // Desktop: centered modal — scale + fade
                top: '50%',
                left: '50%',
                maxWidth: '64rem',
                borderRadius: '1rem',
                width: 'calc(100% - 2rem)',
                transform: isEntered
                  ? 'translate(-50%, -50%) scale(1)'
                  : 'translate(-50%, -50%) scale(0.95)',
                opacity: isEntered ? 1 : 0,
              }
            : {
                // Mobile: slide up from bottom
                bottom: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: '1rem',
                borderTopRightRadius: '1rem',
                transform: isDragging
                  ? `translateY(${dragTransform}px)`
                  : isEntered
                    ? 'translateY(0)'
                    : 'translateY(100%)',
              }),
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle (mobile only) */}
        <div className="flex justify-center pb-2 pt-3 md:hidden">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-4 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1 pr-4">
              <h2 className="type-section-title">{title}</h2>
              {subtitle && (
                <p className="type-caption mt-1 text-gray-600 dark:text-gray-300">{subtitle}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="sm:px-6 sm:py-6">{children}</div>
      </div>
    </>
  );
}
